import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import './App.css';
import './i18n';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { IrrigationProvider } from './contexts/IrrigationContext';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Map from './pages/Map';
import Zones from './pages/Zones';
import Devices from './pages/Devices';
import Alerts from './pages/Alerts';
import Schedules from './pages/Schedules';
import IrrigationHistory from './pages/IrrigationHistory';
import IoTNodes from './pages/IoTNodes';
import WorkOrders from './pages/WorkOrders';
import Settings from './pages/Settings';
import OrgSwitch from './pages/OrgSwitch';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminOrgs from './pages/admin/AdminOrgs';
import TOTPSetupWizard from './pages/TOTPSetupWizard';
import ChangePassword from './pages/ChangePassword';
import Documentation from './pages/Documentation';

// Layout
import Layout from './components/Layout';
import { isPathEnabled } from './config/frontend';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

const ModeRoute: React.FC<{ path: string; children: React.ReactNode }> = ({ path, children }) =>
  isPathEnabled(path) ? <>{children}</> : <Navigate to="/dashboard" replace />;

const AppRoutes: React.FC = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={!isAuthenticated ? <Login /> : <Navigate to="/dashboard" replace />} />
      <Route path="/auth/totp-setup" element={<TOTPSetupWizard />} />
      <Route path="/auth/change-password" element={<ChangePassword />} />
      <Route path="/org/switch" element={<ProtectedRoute><ModeRoute path="/org/switch"><OrgSwitch /></ModeRoute></ProtectedRoute>} />
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="map" element={<ModeRoute path="/map"><Map /></ModeRoute>} />
        <Route path="zones" element={<ModeRoute path="/zones"><Zones /></ModeRoute>} />
        <Route path="devices" element={<Devices />} />
        <Route path="alerts" element={<Alerts />} />
        <Route path="schedules" element={<ModeRoute path="/schedules"><Schedules /></ModeRoute>} />
        <Route path="irrigation-history" element={<ModeRoute path="/irrigation-history"><IrrigationHistory /></ModeRoute>} />
        <Route path="iot" element={<IoTNodes />} />
        <Route path="work-orders" element={<ModeRoute path="/work-orders"><WorkOrders /></ModeRoute>} />
        <Route path="settings" element={<ModeRoute path="/settings"><Settings /></ModeRoute>} />
        <Route path="documentation" element={<Documentation />} />
        
        {/* Admin Routes */}
        <Route path="admin" element={<ModeRoute path="/admin"><AdminDashboard /></ModeRoute>} />
        <Route path="admin/users" element={<ModeRoute path="/admin/users"><AdminUsers /></ModeRoute>} />
        <Route path="admin/orgs" element={<ModeRoute path="/admin/orgs"><AdminOrgs /></ModeRoute>} />
      </Route>
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <IrrigationProvider>
              <Router>
                <div className="App">
                  <AppRoutes />
                  <Toaster position="top-right" richColors />
                </div>
              </Router>
            </IrrigationProvider>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
